import React, { useEffect, useState } from "react";
import { Switch } from "react-router-dom";

import "./App.css";
import { routes } from "@configs";
import { ComponentAppRoute, AppSnackbar } from "@components";
import { IRoute } from "@interfaces";
import { useAppSelector, useAppDispatch } from "./Redux/store";
import firebase from "./firebaseConfig";
import { selectAuth, updateNumberNoti, selectNoti, updateNotiId, resetNoti, fetchTechnicalDocuments, fetchOvertimes, fetchPreloadData } from "@redux";
import { notiApi } from "@api";
import { fetchVehiclesData, fetchVehiclesCategories } from "./Redux/Slices/vehicles";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchEmployeesAndGroups, fetchGroups, fetchTimesheets } from "./Redux/Slices/timesheets";
import { fetchWeeklySchedule } from "./Redux/Slices/weeklySchedule";

function App() {
    const user = useAppSelector(selectAuth);
    const tokenUser = user?.tokenInfoAuth;
    const notifications = useAppSelector(selectNoti);
    const dispatch = useAppDispatch();

    const [maintenance, setMaintenance] = useState<{ message: string; color: string } | null>(null);

    const currentDate = new Date();
    const monthString = currentDate.toISOString().slice(0, 7);
    const dateObject = {
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1, // getMonth() returns 0-based month, so add 1 for 1-based month
    };

    // Clear session storage on page load or refresh
    useEffect(() => {
        sessionStorage.removeItem("userListOrder");
        sessionStorage.removeItem("userListOrderTimesheet");
        sessionStorage.removeItem("userListOrderOvertime");
        sessionStorage.removeItem("userListOrderSchedule");
        sessionStorage.removeItem("groupListOrder");
    }, []);

    useEffect(() => {
        if (tokenUser) {
            // Dispatch the most fetch first
            dispatch(fetchEmployeesAndGroups() as any);
            dispatch(fetchTimesheets({ month: monthString }) as any);
            dispatch(fetchOvertimes({ month: monthString, groups: [] }) as any);
    
            // Dispatch the other actions with a slight delay
            setTimeout(() => {
                dispatch(fetchVehiclesData() as any);
                dispatch(fetchVehiclesCategories() as any);
                dispatch(fetchGroups() as any);
                dispatch(fetchTechnicalDocuments(1) as any);
                dispatch(fetchWeeklySchedule(dateObject) as any);
                dispatch(fetchPreloadData({ userId: user.userInfo?.id }) as any);
            }, 500);
        }
    }, [tokenUser, dispatch]);

    useEffect(() => {
        const checkMaintenance = async () => {
            try {
                const response = await fetch("/maintenance.json", { cache: "no-store" });
                if (response.ok) {
                    const data = await response.json();
                    if (data.message) {
                        setMaintenance({
                            message: data.message,
                            color: data.color || "#b99"
                        });
                    }
                }
            } catch (err) {
                // If file doesn't exist or fetch fails, we assume no maintenance
                setMaintenance(null);
            }
        };

        checkMaintenance();
    }, []);
    
    const closeMaintenance = () => {
        setMaintenance(null);
    };
    
    const maintenanceElement = maintenance ? (
                <div style={{ backgroundColor: maintenance.color, fontSize: "2rem", padding: "10px", textAlign: "center", zIndex: 1200, position: "fixed", top: 0, width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span>{maintenance.message}</span>
                    <button 
                        onClick={closeMaintenance}
                        style={{ background: "rgba(255,255,255,0.8)",  border: "1px solid rgba(0,0,0,0.5)",  fontSize: "1.5rem",  cursor: "pointer", padding: "0.5rem 1rem", borderRadius: "0.5rem"}}
                        aria-label="Close maintenance message"
                    >Close</button>
                </div>
            ) : null;

    // fetch api list noti
    const fetchListNoti = async () => {
        dispatch(resetNoti());
        const res = await notiApi.viewListNotification({ page: 1, status: "" });
        dispatch(updateNumberNoti(res.data.data.amount));
    };

    useEffect(() => {
        if (tokenUser) fetchListNoti();
        return () => {
            // do something cleanup
        };
    }, [tokenUser]);


    let message: firebase.messaging.Messaging;
    useEffect(() => {
        if (!firebase.messaging.isSupported()) {
            console.warn("Firebase messaging is not supported in this browser");
            return;
        }
        message = firebase.messaging();
        const unsubscribe = message.onMessage(() => {
            if (tokenUser) {
                dispatch(updateNumberNoti(notifications.numberNoti + 1));
            }
        });
        return () => {
            unsubscribe();
        };
    }, [tokenUser, dispatch, notifications.numberNoti]);


    return (
        <div className="App">
            <Switch>
                {routes.map((e: IRoute, key) => (
                    <ComponentAppRoute key={key} {...e} />
                ))}
            </Switch>
            <AppSnackbar />
            <ToastContainer />
            {maintenanceElement}
        </div>
    );
}

export default App;
